import React, { useMemo, useRef } from 'react'
import { Form as FinalForm } from 'react-final-form'
import { useHistory } from 'react-router-dom'
import { Grid, Header, Label, Segment } from 'semantic-ui-react'
import { toast } from 'react-toastify'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { Helmet } from 'react-helmet-async'

import { ProductForm } from './ProductForm'
import { API_PRODUCTS, PATH_BATCH_LOOKUP, PATH_PRODUCTS_NEW } from '../../consts'
import { Alias, ProductForm as ProductFormType } from '../../redux/types/products'
import { getInitialProductFormValues } from '../../redux/reducers/ui'
import { to } from '../../utils/to'
import { mutators } from '../../utils/mutators'
import { ProductFlags } from './ProductFlags'
import { Product } from '../../hooks/useProduct'
import { ProductAvailabilityInfo } from './ProductAvailabilityInfo'
import { LookupBar } from './LookupBar'
import { ReadOnlyLocalizations } from '../Localizations/ReadOnlyLocalizations'
import { useLocalizations } from '../../hooks/useLocalizations'
import { CreateActions } from './ProductForm/CreateActions'
import { handleValidationErrors, handleGenericErrors } from '../../utils/Products/errorHandler'
import { validate } from './Form/validate'
import { ImageList } from '@iheartjane/react-image-list'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from '../../redux/store'
import { updateBatchAliasAssignments } from '../../redux/actions/productAliases'
import { batchListMoveDown, setBatchLookupStage } from '../../redux/actions/ui'
import { toProductCreateApiFormat } from './utils/formatPayloads'

/**
 * Takes a product returned from the products api and transforms
 * it to suit the ProductForm component.
 */
const toProductFormFormat = (product: any): ProductFormType | { image_urls: string[] } => {
  if (product === null) {
    return { image_urls: [] }
  }
  const initialProductValues = getInitialProductFormValues()
  Object.keys(initialProductValues).forEach((key: string) => {
    if (product[key]) {
      initialProductValues[key] = product[key]
    }
  })

  // When creating a product we initialize form with no aliases
  initialProductValues['aliases_attributes'] = []

  return initialProductValues
}

export const CreateProduct = ({
  product,
  loading,
}: {
  product: Product | null
  loading: boolean
}) => {
  const dispatch = useDispatch()
  const initialProductFormValues = useMemo(() => toProductFormFormat(product), [product])
  const history = useHistory()
  const productFormPristine = useRef(true)
  const { localizations } = useLocalizations(product?.id)
  const { currentBatchId } = useSelector((state: AppState) => state.ui)
  const pageTitle = product ? `Create Product Like #${product.id}` : 'Create New Product'

  const onSubmit = async (values: ProductFormType) => {
    const payload = toProductCreateApiFormat(values, product)

    const axiosRequestObj: AxiosRequestConfig = {
      url: API_PRODUCTS,
      method: 'POST',
      data: payload,
      headers: { 'content-type': 'application/json' },
      withCredentials: true,
    }

    const [error, response] = await to<AxiosResponse, AxiosError>(axios(axiosRequestObj))

    if (response) {
      const createdProduct = response.data.product
      toast(`Product created with ID ${createdProduct.id}`, {
        containerId: 'default',
        onClick: () => {
          navigator.clipboard.writeText(createdProduct.id).then(() => {
            toast('Product ID copied to the clipboard')
          })
        },
      })

      if (currentBatchId) {
        dispatch(
          updateBatchAliasAssignments({
            batchId: currentBatchId,
            batchSelectedProduct: {
              attributes: {
                ...createdProduct,
                url: `${process.env.REACT_APP_WEB_HOST}/products/${createdProduct.id}/${createdProduct.url_slug}`,
              },
              id: createdProduct.id,
              score: 100,
            },
            batchSelectedProductPhotos: createdProduct.photos,
            batchSelectedProductAliases: createdProduct.aliases.map(
              (aliasItem: Alias) => aliasItem.name
            ),
            isConfirmed: true,
          })
        )
        dispatch(batchListMoveDown())
        dispatch(setBatchLookupStage({ stage: 'review' }))
        history.push(PATH_BATCH_LOOKUP)
      } else {
        history.push(`${PATH_PRODUCTS_NEW}/${createdProduct.id}`)
      }
    } else if (error) {
      const validations = error.response?.data?.errors?.validations
      if (validations) {
        handleValidationErrors(validations)
      } else {
        handleGenericErrors(error)
      }
    }
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Segment basic padded style={{ margin: 0 }} attached="bottom">
        <Header as="h2">
          {product ? (
            <>
              Create a product like "{product?.name}"
              <Label color="grey" onClick={() => navigator.clipboard.writeText(`${product?.id}`)}>
                ID #{product?.id}
              </Label>
            </>
          ) : (
            'Create a product'
          )}
        </Header>
      </Segment>

      <LookupBar
        productId={product?.id}
        loading={loading}
        productFormPristine={productFormPristine}
        edit
      />

      <Segment basic padded style={{ paddingTop: 0, paddingBottom: 0, margin: 0 }}>
        <ProductFlags product={product} refetchProduct={() => {}} />
        <ProductAvailabilityInfo product={product} />
      </Segment>

      <Segment basic padded>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={16}>
              <FinalForm
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialProductFormValues}
                mutators={mutators}
              >
                {(finalFormProps) => (
                  <ImageList initialImages={initialProductFormValues.image_urls}>
                    <ProductForm
                      productFormPristine={productFormPristine}
                      actions={CreateActions}
                      {...finalFormProps}
                    />
                  </ImageList>
                )}
              </FinalForm>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}>
              <ReadOnlyLocalizations localizations={localizations} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
    </>
  )
}
